import { Link } from 'react-router-dom';
import { FaCog } from 'react-icons/fa';
import Button from '@/components/UI/Button';
import StatusBadge from '@/components/UI/StatusBadge';
import {
    describeStrategyInstrumentScope,
    INACTIVE_STRATEGY_LABEL,
    isOptionsStrategy,
    type StrategySubscriptionRow
} from '@/pages/User/PortfolioSettings/components/strategySubscription';
import { PortfolioTypeBadges } from '@/pages/User/PortfolioSettings/components/StrategySubscriptionCells';
import StrategySubscriptionTable from '@/pages/User/PortfolioSettings/components/StrategySubscriptionTable';
import { describeStrategySubscriptionGroup, type StrategySubscriptionGroup } from './strategySubscriptionGroups';

interface StrategySubscriptionGroupCardProps {
    group: StrategySubscriptionGroup;
    onToggleActive: (row: StrategySubscriptionRow) => void;
    onConfigure: (row: StrategySubscriptionRow) => void;
    /** Opens the add-strategy dialog for a portfolio group's portfolio. */
    onAddStrategy: (portfolioId: number) => void;
}

const GroupHeading = ({ group }: { group: StrategySubscriptionGroup }) => {
    if (group.kind === 'portfolio') {
        const { portfolio } = group;

        return (
            <div className="flex flex-wrap items-center gap-2">
                <Link className="text-lg font-semibold text-white hover:text-primary" to={`/user/portfolio/${portfolio.id}`}>
                    {portfolio.name}
                </Link>
                <span className="font-mono text-xs text-zinc-500">#{portfolio.id}</span>
                <PortfolioTypeBadges portfolio={portfolio} />
            </div>
        );
    }

    const { strategy } = group;
    const optionsStrategy = isOptionsStrategy(strategy);

    return (
        <div className="flex flex-wrap items-center gap-2">
            <Link className="text-lg font-semibold text-white hover:text-primary" to={`/user/tradingStrategy/${strategy.id}`}>
                {strategy.name}
            </Link>
            <span className="font-mono text-xs text-zinc-500">#{strategy.id}</span>
            <StatusBadge status={describeStrategyInstrumentScope(strategy)} tone={optionsStrategy ? 'accent' : 'neutral'} />
            {!strategy.active && <StatusBadge status={INACTIVE_STRATEGY_LABEL} tone="warning" />}
        </div>
    );
};

/** One portfolio, or one strategy, with its subscriptions beneath it. */
const StrategySubscriptionGroupCard = ({ group, onToggleActive, onConfigure, onAddStrategy }: StrategySubscriptionGroupCardProps) => (
    <section className="rounded-lg border border-zinc-800 bg-[#1a1a1a] shadow-md">
        <header className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-zinc-800 px-4 py-3">
            <GroupHeading group={group} />
            <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-zinc-400">{describeStrategySubscriptionGroup(group)}</span>
                {group.kind === 'portfolio' && (
                    <>
                        <Button size="sm" variant="primary" onClick={() => onAddStrategy(group.portfolio.id)}>
                            + Add strategy
                        </Button>
                        <Button icon={<FaCog />} size="sm" to={`/user/portfolioSettings/${group.portfolio.id}`} variant="outline-dark">
                            Settings
                        </Button>
                    </>
                )}
            </div>
        </header>

        <div className="p-2 sm:p-3">
            <StrategySubscriptionTable
                identity={group.kind === 'portfolio' ? 'strategy' : 'portfolio'}
                layout="page"
                rows={group.rows}
                search={false}
                onConfigure={onConfigure}
                onToggleActive={onToggleActive}
            />
        </div>
    </section>
);

export default StrategySubscriptionGroupCard;
